import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DropDown from "../DropDown/DropDown";
import CheckboxList from "../CheckboxList/CheckboxList";
import SearchInput from "../SearchInput/SearchInput";
import { SEARCH_LABELS } from "../../utils/Enum";
import { Option } from "../../interfaces/Option";

export interface SelectProps {
  optionList: Option[];
  typeOfField: SEARCH_LABELS;
  isLoading?: boolean;
  onSelect: (value: string[]) => void;
  valueOfField: string[];
  dataTestID?: string;
  id?: string;
}
const Select: React.FC<SelectProps> = ({
  dataTestID,
  optionList,
  typeOfField,
  isLoading,
  onSelect,
  valueOfField,
  id,
}) => {
  const [isDropdown, setIsDropdown] = useState<boolean>(false);
  const [selectedFieldValue, setSelectedFieldValue] =
    useState<Option[]>(optionList);
  const [inputValue, setInputValue] = useState("");
  const [isTyped, setIsTyped] = useState<boolean>(false);
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedFieldValue(optionList);
  }, [optionList]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdown(false); // Collapse dropdown
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleCheckboxChange = (value: string): void => {
    const formattedValue = value.toLowerCase();
    const updatedSelection = valueOfField.includes(formattedValue)
      ? valueOfField.filter((opt: string) => opt !== formattedValue)
      : [...valueOfField, formattedValue];
    if (isSelectAll) {
      setIsSelectAll(false);
    }
    if (updatedSelection.length === optionList.length) {
      setIsSelectAll(true);
    }
    onSelect(updatedSelection);
  };
  const handleSelectAll = () => {
    setIsSelectAll(true);
    onSelect(optionList.map((e: Option) => e.value.toLowerCase()));
  };

  const handleSelectNone = () => {
    setIsSelectAll(false);
    onSelect([]);
  };

  const toggleDropdown = () => {
    setIsDropdown((prev) => !prev);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    const searchValue = event.target.value.toUpperCase();
    setIsTyped(true);
    setSelectedFieldValue(
      optionList.filter((e: Option) => e.label.includes(searchValue)),
    );
  };
  const controlText = useMemo(
    () => `Select ${isSelectAll ? "None" : "All"}`,
    [isSelectAll],
  );

  return (
    <div ref={dropdownRef} className="w-full h-full">
      <DropDown
        isDropdown={isDropdown}
        dataTestID={dataTestID ?? ""}
        toggleDropdown={toggleDropdown}
        valueOfField={valueOfField}
        optionList={optionList}
        id={id}
      >
        <div>
          {typeOfField === SEARCH_LABELS.COMMODITY && (
            <div className="relative h-10">
              <SearchInput
                placeholder={`Search commodities...`}
                value={inputValue}
                onChange={handleInputChange}
              />
              {inputValue.length > 0 && (
                <button
                  className="absolute end-3 top-3 h-6 w-6 pt-[2px] rounded-full bg-gray-600"
                  onClick={() => {
                    setInputValue("");
                    setIsTyped(false);
                  }}
                >
                  <span className="icon-close text-white text-xl" />
                </button>
              )}
            </div>
          )}
          {optionList.length > 0 && (
            <div className="pl-2 pt-2 text-left font-semibold text-CostcoAction">
              <button
                onClick={
                  controlText === "Select All"
                    ? handleSelectAll
                    : handleSelectNone
                }
              >
                {controlText}
              </button>
            </div>
          )}
          <CheckboxList
            options={
              isTyped && inputValue !== "" ? selectedFieldValue : optionList
            }
            valueOfField={valueOfField}
            handleCheckboxChange={handleCheckboxChange}
            isLoading={isLoading}
            isAll={isSelectAll}
          />
        </div>
      </DropDown>
    </div>
  );
};

export default Select;
